import { createContext, useContext, useMemo } from "react";

import { Event } from "../types/event-type";
import { Place } from "../types/place-type";
import { Writer } from "../types/writer-type";
import { CalendarData, getCalendarData } from "./data/calendar-data"; 

const calendarData = getCalendarData((window as any).calendarSourceData);

const CalendarDataContext = createContext(calendarData);

export const CalendarDataProvider = ({
  children,
}: React.PropsWithChildren<unknown>) => {
  return (
    <CalendarDataContext.Provider value={calendarData}>
      {children}
    </CalendarDataContext.Provider>
  );
};

export const useCalendarData = (selector = (value: CalendarData) => value) => {
  const contextValue = useContext(CalendarDataContext);

  return useMemo(() => {
    return selector(contextValue);
  }, [contextValue, selector]);
};

export const useWriters = () => {
  const contextValue = useContext(CalendarDataContext);
  return contextValue.writers;
};

export const useWriter = (id: Writer["id"]) => {
  const contextValue = useContext(CalendarDataContext);
  return contextValue.getWriterById(id);
};

export const usePlaces = () => {
  const contextValue = useContext(CalendarDataContext);
  return contextValue.places;
};

export const usePlace = (id: Place["id"]) => {
  const contextValue = useContext(CalendarDataContext);
  return contextValue.getPlaceById(id);
};

export const useEvents = () => {
  const contextValue = useContext(CalendarDataContext);
  return contextValue.events;
};

export const useEvent = (id: Event["id"]) => {
  const contextValue = useContext(CalendarDataContext);
  return contextValue.getEventById(id);
};

export const useEventWriters = (id: Event["id"]) => {
  const contextValue = useContext(CalendarDataContext);
  const event = useEvent(id);

  return event?.writerIds.map((writerId) =>
    contextValue.getWriterById(writerId)
  ) ?? [];
};

export const useEventPlaces = (id: Event["id"]) => {
  const contextValue = useContext(CalendarDataContext);
  const event = useEvent(id);

  return event?.placesIds.map((placeId) => contextValue.getPlaceById(placeId)) ?? [];
};

export const useYearEvents = () => {
  const contextValue = useContext(CalendarDataContext);
  return contextValue.yearEvents;
}
